/**
 * Framework-agnostic Drupal behavior installer.
 *
 * Installs a single `Drupal.behaviors.<adapter.behaviorName>`
 * entry with attach + detach phases that drive the core mount
 * coordinator and lazy observer. Every SDX framework module
 * goes through this installer so the behavior wiring is
 * written once and any future change (e.g., new detach
 * trigger handling, new dataset flags) lands for every
 * framework at the same time.
 *
 * Scheduling: if `Drupal.behaviors` is already available when
 * the bootstrap runs, the behavior installs immediately.
 * Otherwise the installer waits for DOMContentLoaded - this
 * covers the edge case where a framework module's library
 * script is loaded before Drupal core's own `drupal.js`.
 */

import { LazyMountObserver, findContainers } from './lazy-observer';
import { MountCoordinator, type MountContext } from './mount-coordinator';
import type { FrameworkMountAdapter } from './types';
import type { MountRegistry } from './mount-registry';

let mapMissCheckScheduled = false;

/**
 * Records a componentMap miss and schedules the unclaimed-container check.
 *
 * A container absent from one framework's componentMap may still belong
 * to another framework on a multi-framework page, so no single behavior
 * can tell a legitimate skip from a broken map on its own. Coordination
 * happens through DOM state because every framework bundles its own copy
 * of this module: each skipping behavior appends its log prefix to
 * `data-sdx-map-miss`, a claiming behavior clears it, and a microtask
 * queued after the synchronous attach cycle warns for containers no
 * framework claimed. Without this, an incomplete map (stale render
 * cache, stale build) leaves SSR'd components dead with zero signal -
 * the page looks normal and simply ignores input.
 */
function recordMapMiss(container: HTMLElement, logPrefix: string): void {
  const existing = container.dataset.sdxMapMiss;
  if (!existing) {
    container.dataset.sdxMapMiss = logPrefix;
  }
  else if (!existing.includes(logPrefix)) {
    container.dataset.sdxMapMiss = `${existing} ${logPrefix}`;
  }
  if (mapMissCheckScheduled) return;
  mapMissCheckScheduled = true;
  queueMicrotask(() => {
    mapMissCheckScheduled = false;
    document.querySelectorAll<HTMLElement>('[data-sdx-map-miss]').forEach((el) => {
      const prefixes = el.dataset.sdxMapMiss ?? '';
      delete el.dataset.sdxMapMiss;
      if (el.dataset.sdxClaimed) return;
      const componentId = el.dataset.component ?? '';
      console.warn(
        `${prefixes} No URL published for component: ${componentId} - skipping mount. `
        + "The id is missing from every loaded framework's components map in drupalSettings.",
      );
      // Leave the outcome inspectable from the DOM without touching
      // data-sdx-mounted, so a later attach pass that does publish the
      // URL (SPA navigation, AJAX settings merge) can still mount.
      el.dataset.sdxMapMissReported = prefixes;
    });
  });
}

export interface MountBootstrap<Instance, RuntimeModule> {
  adapter: FrameworkMountAdapter<Instance, RuntimeModule>;
  registry: MountRegistry<Instance>;
  coordinator: MountCoordinator<Instance, RuntimeModule>;
}

/**
 * Constructs the full mount pipeline for a framework adapter
 * and installs its Drupal behavior. Returns the wiring so
 * callers (entry points) can hook additional side effects
 * like `Drupal.sdxNavigation.registerComponentResolver`.
 */
export function bootstrapMount<Instance, RuntimeModule>(
  adapter: FrameworkMountAdapter<Instance, RuntimeModule>,
  registry: MountRegistry<Instance>,
): MountBootstrap<Instance, RuntimeModule> {
  const coordinator = new MountCoordinator(adapter, registry);
  const observer = new LazyMountObserver<MountContext>((container, ctx) => {
    void coordinator.mount(container, ctx);
  });

  registry.publishToDrupalNamespace();

  // Expose a reset API so React-owned scaffolds can invalidate
  // their own mount when they re-render with new content. Typical
  // trigger: MountPointScaffold re-renders with a different
  // `component` or `props`, React re-applies the wrapper div's
  // `dangerouslySetInnerHTML`, and the inner widget root the
  // coordinator placed inside gets wiped - but the coordinator
  // still has the root instance in `mountedRoots` and
  // `data-sdx-mounted` still reads `"true"`. The next
  // `attachBehaviors` pass would then skip the container. Calling
  // `Drupal.sdx.resetMount(el)` from a layout effect in the
  // scaffold clears both the registry entry and the attribute,
  // so the coordinator re-mounts against the fresh scaffold.
  //
  // The implementation calls `coordinator.invalidateMount()`,
  // not `coordinator.unmount()`: the React root's DOM is already
  // gone by the time this fires, so a `root.unmount()` would walk
  // into detached nodes and throw `NotFoundError: removeChild`
  // asynchronously (no try/catch at the call site can intercept
  // it). Dropping the registry entry is enough; GC reclaims the
  // root once nothing references it.
  //
  // Multiple framework adapters (React, Vue, Svelte) chain onto
  // the same handler: each adapter's `invalidateMount()` is a
  // no-op for containers it didn't mount, so stacking the calls
  // is safe.
  if (typeof Drupal !== 'undefined') {
    const sdxNamespace = Drupal.sdx ?? {};
    const previousResetMount = sdxNamespace.resetMount as
      | ((container: HTMLElement) => void)
      | undefined;
    sdxNamespace.resetMount = (container: HTMLElement) => {
      previousResetMount?.(container);
      coordinator.invalidateMount(container);
    };
    Drupal.sdx = sdxNamespace;
  }

  const install = (): void => {
    if (typeof Drupal === 'undefined' || typeof Drupal.behaviors === 'undefined') {
      document.addEventListener('DOMContentLoaded', install, { once: true });
      return;
    }

    Drupal.behaviors[adapter.behaviorName] = {
      attach(context: HTMLElement | Document, settings: DrupalSettings): void {
        const containers = findContainers(context);
        if (containers.length === 0) return;

        const frameworkSettings = adapter.readSettings();
        const mountCtx: MountContext = {
          componentMap: frameworkSettings.components ?? {},
          devServer: frameworkSettings.dev_server ?? null,
        };
        // The `settings` argument is intentionally not consulted
        // here: every adapter's `readSettings()` reads the same
        // `drupalSettings.sdx_<framework>` namespace Drupal core
        // exposes via attachBehaviors, and routing everything
        // through the adapter keeps the core installer free of
        // naming-convention assumptions.
        void settings;

        for (const container of containers) {
          if (container.dataset.sdxMounted) continue;
          // Only mount components THIS framework actually built. On a
          // multi-framework page (e.g. React + Vue both enabled) every
          // framework's behavior sees every `.sdx-component` container.
          // Without this guard the Vue installer would try to mount a
          // React component and fetch a non-existent vue/dist bundle
          // (404). The componentMap - populated from this framework's
          // Vite manifest - is the authoritative set of components it
          // bundled, so a container whose id is absent belongs to another
          // framework and must be left for that framework's behavior.
          const componentId = container.dataset.component;
          if (componentId && !(componentId in mountCtx.componentMap)) {
            if (!container.dataset.sdxClaimed) {
              recordMapMiss(container, adapter.logPrefix);
            }
            continue;
          }
          container.dataset.sdxClaimed = adapter.behaviorName;
          delete container.dataset.sdxMapMiss;
          delete container.dataset.sdxMapMissReported;
          const eager = container.dataset.eager !== undefined;
          if (eager) {
            void coordinator.mount(container, mountCtx);
            continue;
          }
          observer.observe(container, mountCtx, mountCtx);
        }
      },
      detach(context: HTMLElement | Document, _settings: DrupalSettings, trigger: string): void {
        if (trigger !== 'unload') return;
        for (const container of findContainers(context)) {
          coordinator.unmount(container);
          observer.unobserve(container);
        }
      },
    };
  };

  install();
  return { adapter, registry, coordinator };
}

